import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import {
  SideContainer,
  SideToggleButton,
  DesktopSidebarLinks,
  TabletSidebarWrapper,
  TabletSidebarLinks,
} from './SideContainer.styles';

const links = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About Us' },

  {
    label: 'Training',
    children: [
      { path: '/training', label: 'Online Training' },
      { path: '/company-training', label: 'Company Training' },
    ],
  },
  {
    label: 'News & Resources',
    children: [
      { path: '/news', label: 'Newsletter' },
      { path: '/resources', label: 'Resources' },
      { path: '/safety-audits', label: 'Safety Audits' },
      { path: '/safety-manuals', label: 'Safety Manuals' },
      { path: '/digital-safety-forms', label: 'Digital Safety Forms' },
    ],
  },
  {
    label: 'Shop',
    children: [
      {
        path: '/explore-products',
        label: 'Explore Products',
        children: [
          {
            path: '/explore-products?category=material-handling',
            label: 'Material Handling',
          },
          { path: '/explore-products?category=ppe', label: 'PPE' },
          {
            path: '/explore-products?category=spill-response',
            label: 'Spill Response',
          },
          {
            path: '/explore-products?category=tools-worksite-equipment',
            label: 'Tools & Worksite Equipment',
          },
          {
            path: '/explore-products?category=traffic-control',
            label: 'Traffic Control',
          },
          {
            path: '/explore-products?category=workplace-safety',
            label: 'Workplace Safety',
          },
        ],
      },
      { path: '/shop', label: 'How To Purchase' },
    ],
  },

  {
    label: 'Support',
    children: [{ path: '/contact-us', label: 'Contact Us' }],
  },

  {
    label: 'Legal',
    children: [
      { path: '/privacy-policy', label: 'Privacy Policy' },
      { path: '/terms-and-conditions', label: 'Terms & Conditions' },
    ],
  },
];

const SideContainerComponent = () => {
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [tabletOpen, setTabletOpen] = useState(false);
  const location = useLocation();

  const isActiveFor = (to) => {
    if (!to || typeof to !== 'string') return false;
    const [pathname, searchPart] = to.split('?');
    const expectedSearch = searchPart ? `?${searchPart}` : '';
    return location.pathname === pathname && location.search === expectedSearch;
  };

  return (
    <>
      <SideContainer $open={desktopOpen}>
        <SideToggleButton
          type="button"
          aria-label={desktopOpen ? 'Collapse menu' : 'Expand menu'}
          aria-expanded={desktopOpen}
          onClick={() => setDesktopOpen((previous) => !previous)}
        >
          {desktopOpen ? <FaChevronLeft /> : <FaChevronRight />}
        </SideToggleButton>

        <DesktopSidebarLinks $open={desktopOpen}>
          {links.map((link) => (
            <li key={link.path || link.label}>
              {link.children ? (
                <>
                  <div className="parentLink">{link.label}</div>
                  <ul className="subLinks">
                    {link.children.map((child) => (
                      <li key={child.path || child.label}>
                        {child.children ? (
                          <>
                            <NavLink
                              to={child.path}
                              className={() =>
                                isActiveFor(child.path) ? 'active' : ''
                              }
                            >
                              <span>{child.label}</span>
                            </NavLink>
                            <ul className="subLinks nested">
                              {child.children.map((sub) => (
                                <li key={sub.path}>
                                  <NavLink
                                    to={sub.path}
                                    className={() =>
                                      isActiveFor(sub.path) ? 'active' : ''
                                    }
                                  >
                                    <span>{sub.label}</span>
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <NavLink
                            to={child.path}
                            className={() =>
                              isActiveFor(child.path) ? 'active' : ''
                            }
                          >
                            <span>{child.label}</span>
                          </NavLink>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <NavLink
                  to={link.path}
                  className={() => (isActiveFor(link.path) ? 'active' : '')}
                >
                  <span>{link.label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </DesktopSidebarLinks>
      </SideContainer>

      <TabletSidebarWrapper $open={tabletOpen}>
        <SideToggleButton
          type="button"
          aria-label={tabletOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={tabletOpen}
          onClick={() => setTabletOpen((previous) => !previous)}
        >
          {tabletOpen ? <FaChevronLeft /> : <FaChevronRight />}
        </SideToggleButton>

        <TabletSidebarLinks $open={tabletOpen}>
          {links.map((link) => (
            <li key={link.path || link.label}>
              {link.children ? (
                <>
                  <div className="parentLink">{link.label}</div>
                  <ul className="subLinks">
                    {link.children.map((child) => (
                      <li key={child.path || child.label}>
                        {child.children ? (
                          <>
                            <NavLink
                              to={child.path}
                              className={() =>
                                isActiveFor(child.path) ? 'active' : ''
                              }
                              onClick={() => setTabletOpen(false)}
                            >
                              <span>{child.label}</span>
                            </NavLink>
                            <ul className="subLinks nested">
                              {child.children.map((sub) => (
                                <li key={sub.path}>
                                  <NavLink
                                    to={sub.path}
                                    className={() =>
                                      isActiveFor(sub.path) ? 'active' : ''
                                    }
                                    onClick={() => setTabletOpen(false)}
                                  >
                                    <span>{sub.label}</span>
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <NavLink
                            to={child.path}
                            className={() =>
                              isActiveFor(child.path) ? 'active' : ''
                            }
                            onClick={() => setTabletOpen(false)}
                          >
                            <span>{child.label}</span>
                          </NavLink>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <NavLink
                  to={link.path}
                  className={() => (isActiveFor(link.path) ? 'active' : '')}
                  onClick={() => setTabletOpen(false)}
                >
                  <span>{link.label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </TabletSidebarLinks>
      </TabletSidebarWrapper>
    </>
  );
};

export default SideContainerComponent;
